
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PatientData {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  lastAppointment?: string;
  nextAppointment?: string;
  avatarUrl?: string;
  condition?: string;
  progress?: number;
  gender?: string;
  age?: number;
}

export const usePatientData = () => {
  const [patients, setPatients] = useState<PatientData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPatients = async () => {
      setIsLoading(true);
      try {
        // Get patients with their profiles
        const { data, error } = await supabase
          .from('patient_profiles_view')
          .select('*');
          
        if (error) throw error;
        
        if (!data) {
          setPatients([]);
          return;
        }

        // Transform data to match our interface
        const transformedData: PatientData[] = data.map(patient => {
          const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
          
          // Handle medical conditions - extract first condition or set default text
          let conditionText = 'No condition specified';
          
          // Handle the case when medical_conditions can be various types
          if (patient.medical_conditions) {
            if (Array.isArray(patient.medical_conditions) && patient.medical_conditions.length > 0) {
              conditionText = String(patient.medical_conditions[0]);
            } else if (typeof patient.medical_conditions === 'object') {
              // If it's a JSON object, extract first value or key
              const conditionValues = Object.values(patient.medical_conditions);
              if (conditionValues.length > 0) {
                conditionText = String(conditionValues[0]);
              }
            } else if (typeof patient.medical_conditions === 'string') {
              conditionText = patient.medical_conditions;
            }
          }
          
          return {
            id: patient.id || '',
            name: fullName || 'Unknown Patient',
            email: '', // Email is not available in the view
            phone: '', // Phone is not available in the view
            status: patient.status || 'inactive',
            lastAppointment: '', // Will be populated from appointments
            nextAppointment: '', // Will be populated from appointments
            avatarUrl: patient.avatar_url || '',
            condition: conditionText,
            progress: patient.progress || 0,
            gender: patient.gender || '',
            age: patient.date_of_birth 
              ? calculateAge(patient.date_of_birth) 
              : undefined
          };
        });

        // Get appointments to set last and next appointment dates
        const { data: appointmentsData, error: appointmentsError } = await supabase
          .from('appointments')
          .select('patient_id, appointment_date, appointment_time, status')
          .order('appointment_date', { ascending: false });

        if (appointmentsError) {
          console.error('Error fetching appointments:', appointmentsError);
          // Continue without appointments data
        } else if (appointmentsData) {
          // Map appointment dates to patients
          const patientAppointmentsMap = new Map();
          
          appointmentsData.forEach(appointment => {
            if (!patientAppointmentsMap.has(appointment.patient_id)) {
              patientAppointmentsMap.set(appointment.patient_id, { 
                lastAppointment: null,
                nextAppointment: null
              });
            }
            
            const appointmentData = patientAppointmentsMap.get(appointment.patient_id);
            const appointmentDate = new Date(appointment.appointment_date);
            const now = new Date();
            
            // Set last appointment
            if (appointmentDate <= now && (!appointmentData.lastAppointment || 
                appointmentDate > new Date(appointmentData.lastAppointment))) {
              appointmentData.lastAppointment = appointment.appointment_date;
            }
            
            // Set next appointment
            if (appointmentDate >= now && (!appointmentData.nextAppointment || 
                appointmentDate < new Date(appointmentData.nextAppointment))) {
              appointmentData.nextAppointment = appointment.appointment_date;
            }
          });
          
          // Update patients with appointments
          transformedData.forEach(patient => {
            const appointmentData = patientAppointmentsMap.get(patient.id);
            if (appointmentData) {
              patient.lastAppointment = appointmentData.lastAppointment;
              patient.nextAppointment = appointmentData.nextAppointment;
            }
          });
        }

        setPatients(transformedData);
      } catch (err: any) {
        console.error('Error fetching patients:', err);
        setError(err.message);
        toast({
          title: 'Error loading patients',
          description: err.message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatients();
  }, [toast]);

  const calculateAge = (dateOfBirth: string): number => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return { patients, isLoading, error };
};
